/**
 * Cart Drawer Progress Bar
 *
 * Syncs the segmented multi-tier bar to the freshly server-rendered markup: per-segment fill
 * widths, per-node and per-caption tier state, and the current-tier message. Width changes
 * animate via CSS; crossing into a new tier pulses the node and fires a confetti burst.
 *
 * Listens on two channels, because Horizon 4.0 changed how cart mutations are announced:
 *   - `shopify:cart:lines-update` (StandardEvents.cartLinesUpdate) — every native Horizon cart
 *     mutation, e.g. changing a quantity or removing a line inside the drawer. In Horizon 3.5.1
 *     those fired `cart:update`, so this listener is what keeps the bar live after the upgrade.
 *   - `cart:update` — GOAT's own modules (see assets/cart-events.js), which still emit it.
 * Both funnel into #applySections, and re-applying the same sections twice is a no-op.
 */
import { StandardEvents } from '@shopify/events';

const PULSE_CLASS = 'cart-drawer-progress__node--pulse';
const NODE_STATES = ['locked', 'current', 'unlocked'];
const CELEBRATION_CLASS = 'cart-drawer-progress-celebration';
const CONFETTI_COLORS = ['#90bfe6', '#f2c744', '#e8603c', '#4c9f70', '#d3d8df'];
const CONFETTI_PIECES = 36;
const CONFETTI_MS = 1400;

/**
 * Emits a cart analytics event twice: as a DOM CustomEvent on `document` for listener-based
 * tooling, and as a `dataLayer` push for GTM — the convention the rest of the theme already uses
 * (see assets/analytics-events.js).
 *
 * Monetary values are whole currency units, never cents, so a GTM threshold trigger reads the
 * same number the merchant typed into the tier setting.
 *
 * Event names mirror the reference theme, where they are marked provisional pending analytics
 * sign-off. Renaming them later means changing both this file and the GTM triggers.
 *
 *   cart:updated                     — the cart changed
 *   cart-progress:view               — the ladder was rendered with the drawer open
 *   cart-progress:threshold-crossed  — the shopper moved up at least one tier
 *   cart-progress:tier-unlocked      — fired once per newly unlocked tier
 *
 * @param {string} eventName
 * @param {Record<string, unknown>} [detail]
 */
function pushCartAnalytics(eventName, detail = {}) {
  document.dispatchEvent(new CustomEvent(eventName, { detail }));

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...detail });
}

/**
 * Analytics de-duplication, deliberately module-level rather than per-element.
 *
 * One mutation can reach the bar more than once: both announcement channels carry it, and a
 * drawer re-render can briefly leave a second <cart-drawer-progress-bar> connected alongside the
 * first. Each element would then report the same crossing. There is only ever one cart, so the
 * "have I already reported this state?" question belongs to the module, not the instance — while
 * the DOM sync stays per-instance, because every live bar still has to be updated.
 *
 * @type {{ state: string|null, view: string|null }}
 */
const reported = { state: null, view: null };

/**
 * @param {Element} el
 * @param {string} name
 * @returns {number}
 */
function centsToUnits(el, name) {
  const cents = Number(/** @type {HTMLElement} */ (el).dataset[name] ?? 0) || 0;
  return cents / 100;
}

class CartDrawerProgressBar extends HTMLElement {
  /** @type {Record<string, string>|null} Last `sections` object applied, to skip the duplicate dispatch. */
  #lastAppliedSections = null;

  /** @type {NodeListOf<HTMLElement>} */
  #fills = [];

  /** @type {NodeListOf<HTMLElement>} */
  #nodes = [];

  /** @type {NodeListOf<HTMLElement>} */
  #labels = [];

  /** @type {HTMLElement|null} */
  #messageEl = null;

  /** @type {Function|null} */
  #boundUpdate = null;

  /** @type {Function|null} */
  #boundStandardUpdate = null;

  /** @type {Function|null} */
  #boundDrawerOpen = null;

  /** @type {Function|null} */
  #boundDrawerClose = null;

  /**
   * Unlocked-tier count as of the last render. `null` until the first measurement, so the tiers a
   * shopper unlocked on an earlier visit are not reported as crossings the moment the drawer opens.
   * @type {number|null}
   */
  #unlockedCount = null;

  /** @type {boolean} */
  #trackedView = false;

  /** @type {number|undefined} */
  #viewTimer;

  /** @type {boolean} */
  #listening = false;

  connectedCallback() {
    this.#cacheRefs();

    // Only on the first mount. A cart mutation morphs the drawer, which can re-adopt this element
    // and run `connectedCallback` again — re-reading the count from the DOM there would reset the
    // crossing baseline and report the same threshold a second time.
    if (this.#unlockedCount === null) this.#unlockedCount = this.#readUnlockedCount();

    if (this.#listening) return;
    this.#listening = true;

    this.#boundUpdate = this.#handleCartUpdate.bind(this);
    document.addEventListener('cart:update', this.#boundUpdate);

    this.#boundStandardUpdate = this.#handleStandardCartUpdate.bind(this);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#boundStandardUpdate);

    // The bar is server-rendered into a closed <dialog> on every page load, so mounting is not a
    // view. The drawer's own open/close events are, and closing re-arms it for the next open.
    this.#boundDrawerOpen = this.#handleDrawerOpen.bind(this);
    this.#boundDrawerClose = this.#handleDrawerClose.bind(this);
    document.addEventListener('theme-drawer:open', this.#boundDrawerOpen);
    document.addEventListener('theme-drawer:close', this.#boundDrawerClose);

    // On the /cart page the bar is not inside a drawer at all, so nothing will ever open it.
    if (!this.closest('theme-drawer')) this.#trackView();
  }

  disconnectedCallback() {
    this.#listening = false;
    clearTimeout(this.#viewTimer);

    if (this.#boundUpdate) {
      document.removeEventListener('cart:update', this.#boundUpdate);
    }

    if (this.#boundStandardUpdate) {
      document.removeEventListener(StandardEvents.cartLinesUpdate, this.#boundStandardUpdate);
    }

    if (this.#boundDrawerOpen) {
      document.removeEventListener('theme-drawer:open', this.#boundDrawerOpen);
    }

    if (this.#boundDrawerClose) {
      document.removeEventListener('theme-drawer:close', this.#boundDrawerClose);
    }
  }

  /**
   * @param {Event} event
   */
  #handleDrawerOpen(event) {
    const drawer = /** @type {Element|null} */ (event.target);
    if (!drawer || !drawer.contains(this)) return;

    /*
     * Deferred by one task, not reported here. An add-to-cart opens the drawer SYNCHRONOUSLY while
     * the cart event is still dispatching (assets/cart-drawer.js opens before the event's promise
     * resolves), so the bar's attributes still describe the previous cart at this point — the
     * shopper would see "1 item, $60, tier 1 unlocked" while the impression said the cart was
     * empty. A macrotask runs after the promise microtasks that apply the fresh sections.
     */
    clearTimeout(this.#viewTimer);
    this.#viewTimer = setTimeout(() => this.#trackView(), 0);
  }

  /**
   * @param {Event} event
   */
  #handleDrawerClose(event) {
    const drawer = /** @type {Element|null} */ (event.target);
    if (!drawer || !drawer.contains(this)) return;
    clearTimeout(this.#viewTimer);
    this.#trackedView = false;
    reported.view = null;
  }

  /**
   * Horizon 4.x cart mutations resolve their section HTML on the event's promise rather than
   * exposing it on `detail` synchronously.
   *
   * @param {{ promise?: Promise<{ detail?: { sections?: Record<string, string> } }> }} event
   */
  #handleStandardCartUpdate(event) {
    event.promise
      ?.then(({ detail }) => {
        const sections = detail?.sections;
        if (sections) this.#applySections(sections);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-drawer-progress-bar] Event promise rejected:', error);
        }
      });
  }

  /**
   * Tiers currently unlocked, read from the server-rendered attribute rather than counted from the
   * DOM so it cannot drift from what Liquid decided.
   * @returns {number}
   */
  #readUnlockedCount() {
    return Number(this.dataset.unlockedCount ?? 0) || 0;
  }

  /**
   * Fires once per drawer open. The empty-cart drawer renders a bar too, and a ladder with no
   * tiers is not worth reporting.
   */
  #trackView() {
    if (this.#trackedView) return;

    // The deferred report lands a task later, by which time the drawer may be shut again.
    const drawer = this.closest('theme-drawer');
    if (drawer && !drawer.hasAttribute('open')) return;

    const tierCount = Number(this.dataset.tierCount ?? 0) || 0;
    if (!tierCount) return;

    const key = `${tierCount}|${this.dataset.unlockedCount}|${this.dataset.thresholdSubtotal}`;
    if (key === reported.view) return;
    reported.view = key;

    this.#trackedView = true;
    pushCartAnalytics('cart-progress:view', {
      tier_count: tierCount,
      unlocked_count: this.#readUnlockedCount(),
      subtotal: centsToUnits(this, 'thresholdSubtotal'),
    });
  }

  /**
   * Reports the cart itself, from whichever render just landed.
   * @param {Element} freshBar
   */
  #trackCartUpdate(freshBar) {
    pushCartAnalytics('cart:updated', {
      item_count: Number(/** @type {HTMLElement} */ (freshBar).dataset.itemCount ?? 0) || 0,
      subtotal: centsToUnits(freshBar, 'subtotal'),
      threshold_subtotal: centsToUnits(freshBar, 'thresholdSubtotal'),
      total_discount: centsToUnits(freshBar, 'totalDiscount'),
    });
  }

  /**
   * Reports tiers unlocked since the last cart update. Falling back below a threshold — removing an
   * item — updates the baseline without reporting anything, so re-crossing it reports once more.
   *
   * @param {Element} freshBar - The newly rendered bar, whose nodes describe the new state.
   * @param {boolean} report - False when another pass already reported this cart state.
   * @returns {boolean} Whether the shopper moved up at least one tier.
   */
  #trackCrossings(freshBar, report) {
    const previous = this.#unlockedCount;
    const current = Number(/** @type {HTMLElement} */ (freshBar).dataset.unlockedCount ?? 0) || 0;
    this.#unlockedCount = current;

    if (previous === null || current <= previous) return false;
    if (!report) return true;

    pushCartAnalytics('cart-progress:threshold-crossed', {
      unlocked_count: current,
      is_complete: /** @type {HTMLElement} */ (freshBar).dataset.complete === 'true',
      subtotal: centsToUnits(freshBar, 'thresholdSubtotal'),
    });

    // One event per tier actually crossed, in ladder order. Every tier owns exactly one node, so
    // tiers that share a threshold each report separately.
    const nodes = Array.from(freshBar.querySelectorAll('.cart-drawer-progress__node'));
    for (const node of nodes.slice(previous, current)) {
      const { threshold, tierType, reward } = /** @type {HTMLElement} */ (node).dataset;
      pushCartAnalytics('cart-progress:tier-unlocked', {
        threshold: Number(threshold ?? 0) || 0,
        reward: reward || '',
        tier_type: tierType || '',
      });
    }

    return true;
  }

  #cacheRefs() {
    this.#fills = this.querySelectorAll('.cart-drawer-progress__seg-fill');
    this.#nodes = this.querySelectorAll('.cart-drawer-progress__node');
    this.#labels = this.querySelectorAll('.cart-drawer-progress__label');
    this.#messageEl = this.querySelector('.cart-drawer-progress__message');
  }

  /**
   * @param {CustomEvent} event
   */
  #handleCartUpdate(event) {
    const sections = event.detail?.data?.sections;

    if (!sections) {
      return;
    }

    this.#applySections(sections);
  }

  /**
   * Swaps a state suffix class (`--locked` / `--current` / `--unlocked`) on one element.
   * @param {HTMLElement} el
   * @param {string} base
   * @param {string|undefined} state
   */
  #applyState(el, base, state) {
    for (const candidate of NODE_STATES) {
      el.classList.toggle(`${base}--${candidate}`, candidate === state);
    }
  }

  /**
   * @param {Record<string, string>} sections - section id -> re-rendered HTML
   */
  #applySections(sections) {
    /*
     * GOAT (perf): every GOAT-module mutation announces twice — `CartLinesUpdateEvent` and the
     * legacy `cart:update` — and this element listens for both, so it used to DOMParser-parse the
     * full drawer HTML and rewrite its own nodes twice per cart change. Both dispatches carry the
     * very same `sections` object, so identity is enough to skip the duplicate pass; a genuinely
     * new mutation always brings a new object.
     */
    if (sections === this.#lastAppliedSections) return;
    this.#lastAppliedSections = sections;

    for (const [, html] of Object.entries(sections)) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const freshBar = doc.querySelector('cart-drawer-progress-bar');

      if (!freshBar) {
        continue;
      }

      const fresh = /** @type {HTMLElement} */ (freshBar).dataset;
      const fingerprint = `${fresh.itemCount}|${fresh.thresholdSubtotal}|${fresh.unlockedCount}|${fresh.tierCount}`;
      const isNewState = fingerprint !== reported.state;

      if (isNewState) {
        reported.state = fingerprint;
        // A new cart state is a new view, so the drawer can report it again.
        reported.view = null;
      }

      // Re-cache in case the bar was re-rendered (tier count can change).
      this.#cacheRefs();

      // Before the live nodes are updated, while their previous state is still readable. The
      // baseline advances on every pass so this element stays in step; only the reporting is gated.
      const crossed = this.#trackCrossings(freshBar, isNewState);
      if (isNewState) this.#trackCartUpdate(freshBar);

      // Carry the fresh cart figures onto the live element so a later view event reports them.
      for (const key of ['subtotal', 'thresholdSubtotal', 'itemCount', 'totalDiscount', 'unlockedCount', 'tierCount', 'complete']) {
        const value = /** @type {HTMLElement} */ (freshBar).dataset[key];
        if (value !== undefined) this.dataset[key] = value;
      }

      // Sync per-segment fill widths (CSS transitions the change).
      const freshFills = freshBar.querySelectorAll('.cart-drawer-progress__seg-fill');
      for (let i = 0; i < this.#fills.length && i < freshFills.length; i++) {
        this.#fills[i].style.width = /** @type {HTMLElement} */ (freshFills[i]).style.width;
      }

      // Sync per-node tier state, pulsing nodes that just unlocked.
      const freshNodes = freshBar.querySelectorAll('.cart-drawer-progress__node');
      for (let i = 0; i < this.#nodes.length && i < freshNodes.length; i++) {
        const liveNode = this.#nodes[i];
        const freshNode = /** @type {HTMLElement} */ (freshNodes[i]);
        const wasUnlocked = liveNode.dataset.state === 'unlocked';
        const state = freshNode.dataset.state;

        liveNode.dataset.state = state ?? 'locked';
        liveNode.dataset.reward = freshNode.dataset.reward ?? '';
        this.#applyState(liveNode, 'cart-drawer-progress__node', state);

        if (state === 'unlocked' && !wasUnlocked) {
          liveNode.classList.add(PULSE_CLASS);
          liveNode.addEventListener('animationend', () => liveNode.classList.remove(PULSE_CLASS), {
            once: true,
          });
        }
      }

      // Sync captions: their copy carries tokens that move with the cart, and their weight and
      // colour carry the qualified state.
      const freshLabels = freshBar.querySelectorAll('.cart-drawer-progress__label');
      for (let i = 0; i < this.#labels.length && i < freshLabels.length; i++) {
        const liveLabel = this.#labels[i];
        const freshLabel = /** @type {HTMLElement} */ (freshLabels[i]);
        // innerHTML so merchant richtext (<strong>, <em>) survives. Theme-rendered, no shopper input.
        liveLabel.innerHTML = freshLabel.innerHTML;

        const state = NODE_STATES.find((candidate) =>
          freshLabel.classList.contains(`cart-drawer-progress__label--${candidate}`)
        );
        this.#applyState(liveLabel, 'cart-drawer-progress__label', state);
      }

      // Update the current-tier message.
      const freshMessage = freshBar.querySelector('.cart-drawer-progress__message');
      if (freshMessage && this.#messageEl) {
        // innerHTML so the emphasized goal label (<strong>) is preserved. Theme-rendered, no user input.
        this.#messageEl.innerHTML = freshMessage.innerHTML;
      }

      if (crossed && isNewState) this.#celebrate();

      break;
    }
  }

  /**
   * Confetti burst for a threshold crossing. Hand-rolled on the Web Animations API rather than a
   * Lottie bundle, and mounted on the drawer dialog because the bar itself sits inside the
   * cart-drawer hydration-morph target, which reconciles children it did not render away.
   */
  #celebrate() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const host = this.closest('dialog') ?? this.parentElement;
    if (!host) return;

    host.querySelector(`.${CELEBRATION_CLASS}`)?.remove();

    const layer = document.createElement('div');
    layer.className = CELEBRATION_CLASS;
    layer.setAttribute('aria-hidden', 'true');

    const bar = this.getBoundingClientRect();
    const hostBox = host.getBoundingClientRect();
    const originX = bar.left - hostBox.left + bar.width / 2;
    const originY = bar.top - hostBox.top + bar.height / 2;

    for (let i = 0; i < CONFETTI_PIECES; i++) {
      const piece = document.createElement('span');
      piece.className = `${CELEBRATION_CLASS}__piece`;
      piece.style.backgroundColor = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      piece.style.transform = `translate(${originX}px, ${originY}px)`;
      layer.appendChild(piece);
    }

    host.appendChild(layer);

    const pieces = Array.from(layer.children);
    let settled = 0;

    for (const piece of pieces) {
      const angle = Math.random() * Math.PI - Math.PI / 2;
      const distance = 90 + Math.random() * 190;
      const endX = originX + Math.cos(angle) * distance;
      const endY = originY + Math.sin(angle) * distance + 120;
      const spin = Math.round(Math.random() * 720 - 360);

      const animation = piece.animate(
        [
          { transform: `translate(${originX}px, ${originY}px) rotate(0deg)`, opacity: 1 },
          {
            transform: `translate(${endX}px, ${endY}px) rotate(${spin}deg)`,
            opacity: 0,
          },
        ],
        {
          duration: CONFETTI_MS * (0.7 + Math.random() * 0.3),
          easing: 'cubic-bezier(0.2, 0.7, 0.4, 1)',
          fill: 'forwards',
        }
      );

      animation.onfinish = () => {
        settled++;
        if (settled === pieces.length) layer.remove();
      };
    }

    // Belt and braces: if a piece never reports finishing (a backgrounded tab pauses the
    // animation), the layer still has to go rather than sitting over the drawer forever.
    window.setTimeout(() => layer.remove(), CONFETTI_MS * 2);
  }
}

if (!customElements.get('cart-drawer-progress-bar')) {
  customElements.define('cart-drawer-progress-bar', CartDrawerProgressBar);
}
